import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, PenTool, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { API } from "@/lib/api";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

export const MerchantSignPage = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchInvoice(); }, [token]);

  useEffect(() => {
    if (invoice && canvasRef.current) initCanvas();
  }, [invoice]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const fetchInvoice = async () => {
    try {
      const res = await fetch(`${API}/vendor-invoices/sign/${token}`);
      if (res.status === 404) { setError("invalid"); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.signature_status === "Signed") { setError("already_signed"); return; }
      setInvoice(data);
    } catch {
      setError("invalid");
    } finally {
      setLoading(false);
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    if (isDrawing && canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL("image/png"));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => { initCanvas(); setSignatureData(""); };

  const handleSubmit = async () => {
    if (!signatureData) { toast.error("Please sign before submitting"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/vendor-invoices/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data: signatureData }),
      });
      if (!res.ok) {
        let msg = "Failed to submit";
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || "Failed to submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 animate-pulse text-sm">Loading invoice...</div>
      </div>
    );
  }

  if (error === "invalid") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4 text-3xl">❌</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-500 text-sm">This signing link is invalid or has expired. Please contact your RM for a new link.</p>
        </div>
      </div>
    );
  }

  if (error === "already_signed") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Already Signed</h1>
          <p className="text-slate-500 text-sm">This invoice has already been signed. No further action is needed.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Signature Submitted!</h1>
          <p className="text-slate-500 text-sm">Thank you. Your signature has been recorded and the invoice is now finalized.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#0047FF] flex items-center justify-center">
            <PenTool className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900 text-sm">Pokus Invoice Sign</h1>
            <p className="text-xs text-slate-500">{invoice.invoice_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Order Summary</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Vendor</span>
              <span className="font-medium text-slate-900">{invoice.vendor_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Billed To</span>
              <span className="font-medium text-slate-900 text-right">
                Pokus AI (Pokus Technologies Pvt Ltd)
                <span className="block text-xs font-normal text-slate-500">GSTIN: 29AAQCP4908B1Z8</span>
              </span>
            </div>
            {invoice.customer_name && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Customer</span>
                <span className="font-medium text-slate-900">{invoice.customer_name}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Date</span>
              <span className="font-medium text-slate-900">{format(new Date(invoice.invoice_date), "dd MMM yyyy")}</span>
            </div>
            {invoice.rm_name && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">RM</span>
                <span className="font-medium text-slate-900">{invoice.rm_name}</span>
              </div>
            )}
          </div>
          <div className="border-t border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-2 text-xs text-slate-400 font-medium">Service</th>
                  <th className="text-center px-2 py-2 text-xs text-slate-400 font-medium">Qty</th>
                  <th className="text-right px-2 py-2 text-xs text-slate-400 font-medium">Rate</th>
                  <th className="text-right px-4 py-2 text-xs text-slate-400 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items || []).map((item, i) => {
                  const cp = item.cost_price || item.price_per_unit || 0;
                  const amt = item.quantity * cp;
                  return (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-4 py-2.5 text-slate-700">{item.service_type}</td>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{item.quantity}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-slate-500">{formatCurrency(cp)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-700">{formatCurrency(amt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 space-y-1.5 border-t border-slate-200 bg-slate-50">
            <div className="flex justify-between text-sm text-slate-500"><span>Subtotal</span><span>{formatCurrency(invoice.vendor_subtotal || invoice.subtotal)}</span></div>
            {invoice.delivery_fee > 0 && <div className="flex justify-between text-sm text-slate-500"><span>Delivery</span><span>+{formatCurrency(invoice.delivery_fee)}</span></div>}
            <div className="flex justify-between font-bold text-slate-900 pt-1.5 border-t border-slate-200 mt-1">
              <span>Total Payable</span>
              <span>{formatCurrency(invoice.vendor_total || invoice.total_payable)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PenTool className="w-4 h-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-700">Your Signature</p>
            </div>
            <button onClick={clearSignature} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
          <div className="p-4">
            <p className="text-xs text-slate-500 mb-3">Sign in the box below to confirm the above order details</p>
            <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                className="w-full touch-none cursor-crosshair"
                style={{ touchAction: "none" }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            {signatureData && (
              <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Signature captured
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || !signatureData}
          className={`w-full py-4 rounded-xl font-semibold text-base transition-all ${
            signatureData && !submitting
              ? "bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Submitting..." : "Submit Signature & Confirm"}
        </button>

        <p className="text-center text-xs text-slate-400 pb-6">
          By signing, you confirm the order details above are correct.
        </p>
      </div>
    </div>
  );
};
